import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faExclamationCircle,
  faRepeatAlt,
} from '@fortawesome/sharp-regular-svg-icons'
import {
  Box,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core'
import React, { ReactElement } from 'react'
import styled from 'styled-components'

import Spinner from '../../../../1stlog-ui/src/components/Spinner'
import { getFigmaColors } from '../../../utils/getFigmaColors'
import { useGetStatistics } from './queries'

type StatisticsProps = {
  setFilter: (opts: { name: string; value: string | undefined }) => void
  currentFilter?: { field: string; value: string | undefined }[]
  applyFilter: (opts: { reset: { reset: boolean } }) => void
}

const Wrapper = styled.div`
  flex: 1;
  display: 'flex';
  justifycontent: 'center';
  maxwidth: 200px;

  &:hover {
    border: 2px solid red;
  }
`

export const Statistics = (props: StatisticsProps): ReactElement => {
  const { setFilter, currentFilter, applyFilter } = props

  const theme = useMantineTheme()

  const { data, isLoading, isFetching } = useGetStatistics()

  const currentState = currentFilter?.find((f) => f.field === 'state')?.value

  const onFilter = (type: string): void => {
    if (type === currentState) {
      setFilter({
        name: 'state',
        value: '',
      })
    } else {
      setFilter({
        name: 'state',
        value: type,
      })
    }
    setTimeout(() => {
      applyFilter({
        reset: {
          reset: false,
        },
      })
    }, 0)
  }

  return (
    <>
      <Box p="xl">
        <Box>
          <Text hidden className="text-muted">
            Nur sichtbar für localhost/local!
          </Text>
          <Group align="stretch">
            <Widget
              icon={
                <FontAwesomeIcon size="3x" icon={faCheckCircle} color="green" />
              }
              text={i18n.t(
                'shipments_successfully_delivered',
                'Sendungen erfolgreich zugestellt',
              )}
              value={data?.delivered}
              color={theme.colors.green}
              type="delivered"
              onFilter={onFilter}
              isActive={currentState === 'delivered'}
            />
            <Widget
              icon={
                <FontAwesomeIcon size="3x" icon={faRepeatAlt} color="orange" />
              }
              text={i18n.t(
                'more_delivery_trips_required',
                'Weitere Zustellfahrten erforderlich',
              )}
              value={data?.deliveryIssue}
              color={theme.colors.yellow}
              type="deliveryIssue"
              onFilter={onFilter}
              isActive={Boolean(
                currentState &&
                  ['deliveryIssue', 'pickupIssue'].includes(currentState),
              )}
            />
            <Widget
              icon={
                <FontAwesomeIcon
                  size="3x"
                  icon={faExclamationCircle}
                  color="red"
                />
              }
              text={i18n.t('status_negative', 'Negativereignisse')}
              value={data?.negative}
              color={theme.colors.red}
              type="negative"
              onFilter={onFilter}
              isActive={currentState === 'negative'}
            />
          </Group>
        </Box>
      </Box>
    </>
  )
}

function Widget(props: {
  icon: ReactElement
  text: string
  value?: string | number
  color?: string[]
  type: string
  onFilter: (type: string) => void
  isActive?: boolean
}): any {
  const { icon, text, value, color, type, onFilter, isActive } = props

  const { darkBlue, brightBlue, grey } = getFigmaColors()
  return (
    <Tooltip
      label={
        isActive
          ? i18n.t('remove_stat_filter', 'Filter aufheben')
          : i18n.t('filter_for_this_stat', 'Nach diesem Wert filtern')
      }
      withArrow
      withinPortal
    >
      <Paper
        onClick={() => onFilter(type)}
        withBorder={false}
        px="lg"
        py="xs"
        style={{
          maxWidth: '200px',
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          cursor: 'pointer',
          border: '1px solid',
          borderColor: isActive && color ? color[7] : 'transparent',
          // borderColor: 'transparent',
        }}
        radius="lg"
        // bg={color ? chroma(color[1]).brighten(0.9).hex() : 'white'}
      >
        <Stack
          spacing={3}
          align="center"
          justify="center"
          // style={{ border: '2px solid lime' }}
        >
          {value === undefined ? (
            <Spinner />
          ) : (
            <Text fw="bold" size="xl" color={color ? color[7] : darkBlue}>
              {value}
            </Text>
          )}
          <Text align="center" color={grey}>
            {text}
          </Text>
        </Stack>
      </Paper>
    </Tooltip>
  )
}
